import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, Clock } from 'lucide-react';
import { ptArticles } from '@/mocks/articles/pt';
import { enArticles } from '@/mocks/articles/en';
import { deArticles } from '@/mocks/articles/de';
import { articleSlugs } from '@/mocks/articleList';

const articlePaths: Record<string, string> = {
  pt: '/artigo/',
  en: '/article/',
  de: '/artikel/',
};

export default function PillarArticles() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'pt';

  const articlesByLang: Record<string, any> = {
    pt: ptArticles,
    en: enArticles,
    de: deArticles,
  };
  const articles = articlesByLang[currentLang] || ptArticles;

  const goToArticle = (slug: string) => {
    navigate(`${articlePaths[currentLang]}${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-brand-600" />
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900">{t('pillarTitle')}</h2>
            <p className="text-sm text-warm-500 mt-0.5">{t('pillarSubtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articleSlugs.map((slug, idx) => {
            const article = articles[slug];
            if (!article) return null;
            return (
              <button
                key={slug}
                type="button"
                className="group text-left p-5 rounded-xl border border-warm-200 bg-warm-50 hover:border-brand-300 hover:bg-brand-50 transition-all duration-200 cursor-pointer"
                onClick={() => goToArticle(slug)}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 text-sm font-bold flex items-center justify-center flex-shrink-0">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-warm-400 group-hover:text-brand-500 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
                <h3 className="font-medium text-warm-800 group-hover:text-brand-700 leading-snug mb-2">
                  {article.title}
                </h3>
                <p className="text-xs text-warm-500 leading-relaxed line-clamp-2 mb-3">{article.metaDescription}</p>
                <div className="flex items-center gap-1.5 text-xs text-warm-400">
                  <Clock className="w-3.5 h-3.5" />
                  {article.readingTime}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}